import RoundedButton from 'components/RoundedButton';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import { Message } from 'semantic-ui-react';
import { DEFAULT_MESSAGE } from 'utils/account/DefaultTicketMessage';
import { stringFromDate } from 'utils/common';
import { ASSET_PREFIX } from 'utils/constants';
import { ENTER_CTA_LINK } from 'utils/cta/CTA';
import {
  activeSubscriptionStates,
  inactiveSubscriptionStates,
} from 'utils/subscription/State';
import { EntriesSection } from './EntriesSection';

interface ISubscriptionStateInfo {
  excludedStates?: string[]; // return null for states
}

const NoSubscription = () => (
  <div className="flex flex-col justify-center gap-6">
    <p className="text-bold mb-0 text-md">
      This account is not currently subscribed to any plan.
    </p>
    <RoundedButton href={ENTER_CTA_LINK}>ENTER NOW</RoundedButton>
  </div>
);

export const SubscriptionStateInfo = observer(
  ({ excludedStates = [] }: ISubscriptionStateInfo) => {
    const {
      currentUser,
      updateCurrentUser,
      logout,
      activeSubscriptions,
      activeWeeklyDrawTickets,
      isOperator,
      initialized,
    } = useUserStore();

    const numberOfTicketsToShow =
      activeSubscriptions[0]?.drawSchedulePlans.length;

    if (activeSubscriptions.length === 0) {
      return <NoSubscription />;
    }

    const subscription = activeSubscriptions[0];

    const paypalSubscription = subscription.order.paymentMethod === 'PAYPAL';

    if (
      inactiveSubscriptionStates.includes(subscription.subscription.status) &&
      !excludedStates.includes(subscription.subscription.status)
    ) {
      // Handle non-active subscriptions
      switch (subscription.subscription.status) {
        case 'FAILED':
          return (
            <div className="headerRow">
              <Message
                color="red"
                icon="exclamation"
                header="Subscription Failed"
                content="This subscription has failed (e.g., because payments are being rejected). For more information, contact us at hello@daymade.co.uk"
              />
            </div>
          );
        case 'CANCELLED':
        case 'INACTIVE':
          return (
            <div className="w-full flex flex-col gap-4">
              <NoSubscription />
              <EntriesSection
                showMessage={false}
                tickets={activeWeeklyDrawTickets}
                loading={!initialized}
                numberOfTicketsToShow={activeWeeklyDrawTickets.length}
              />
            </div>
          );

        default:
          return null;
      }
    }
    if (
      activeSubscriptionStates.includes(subscription.subscription.status) &&
      !excludedStates.includes(subscription.subscription.status)
    ) {
      switch (subscription.subscription.status) {
        case 'FAILING':
          return (
            <>
              <Message
                color="yellow"
                icon="exclamation"
                header="Subscription Failing"
                content={
                  <Message.Content>
                    This subscription is failing (e.g. because payments are
                    being rejected). We will retry processing this subscription
                    shortly.{' '}
                    {!paypalSubscription &&
                      subscription.drawType.code === 'DAYMADEDRAW' && (
                        <>
                          <Link
                            href={`/account/subscriptions/change-payment-method?id=${subscription.subscription.id}`}
                          >
                            <a
                              style={{
                                color: 'inherit',
                                textDecoration: 'underline',
                              }}
                            >
                              Click here
                            </a>
                          </Link>{' '}
                          to try a different payment method.
                        </>
                      )}{' '}
                    For more information, drop us an email:{' '}
                    <a
                      href="mailto:hello@daymade.co.uk"
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                      hello@daymade.co.uk
                    </a>
                  </Message.Content>
                }
              />
              <EntriesSection
                showMessage={false}
                tickets={activeWeeklyDrawTickets}
                loading={!initialized}
                numberOfTicketsToShow={activeWeeklyDrawTickets.length}
              />
            </>
          );

        case 'SNOOZED':
          return (
            <>
              <Message
                size="large"
                color="blue"
                content={
                  <Message.Content className="w-full flex gap-4 py-2">
                    <div className="flex justify-center items-center">
                      <img
                        src={`${ASSET_PREFIX}/images/snooze-switch-section/moon.svg`}
                        alt=""
                        style={{
                          height: 30,
                        }}
                      />
                    </div>
                    <div>
                      Your subscription is currently snoozed
                      {subscription.subscription.snoozedUntil
                        ? ` until ${stringFromDate(subscription.subscription.snoozedUntil)}`
                        : ''}
                      . You can resume{' '}
                      <Link href={'/account/subscriptions'}>
                        <a
                          style={{
                            color: 'inherit',
                            textDecoration: 'underline',
                          }}
                        >
                          here
                        </a>
                      </Link>
                      .
                    </div>
                  </Message.Content>
                }
              />
              <EntriesSection
                showMessage={false}
                tickets={activeWeeklyDrawTickets}
                loading={!initialized}
                numberOfTicketsToShow={activeWeeklyDrawTickets.length}
              />
            </>
          );

        case 'ACTIVE':
          return (
            <EntriesSection
              showMessage={true}
              tickets={activeWeeklyDrawTickets}
              loading={!initialized}
              numberOfTicketsToShow={activeWeeklyDrawTickets.length}
            />
          );
        default:
          return null;
      }
    }

    return <p className="text-center">{DEFAULT_MESSAGE}</p>;
  }
);
